import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';
import { Send, Users, Calendar, History, RefreshCw, FileText, Code } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = getBackendUrl();

const AdminBulkEmail = () => {
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);
  
  const [formData, setFormData] = useState({
    event_id: '',
    include_all_events: false,
    subject: '',
    header_text: 'Announcement',
    content: '',
    html_content: ''
  });

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchRecipientCount();
  }, [formData.event_id, formData.include_all_events]);

  const fetchData = async () => {
    try {
      const [eventsRes, logsRes] = await Promise.all([
        fetch(`${API}/admin/booking/events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/bulk-email/logs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const eventsData = await eventsRes.json();
      const logsData = await logsRes.json();
      
      if (eventsData.success) {
        setEvents(eventsData.events || []);
      }
      if (logsData.success) {
        setLogs(logsData.logs || []);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientCount = async () => {
    try {
      let url = `${API}/admin/bulk-email/recipients`;
      if (formData.event_id && !formData.include_all_events) {
        url += `?event_id=${formData.event_id}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setRecipientCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch recipient count');
    }
  };

  const handleSend = async () => {
    // Validate based on mode
    if (useAdvancedMode) {
      if (!formData.html_content?.trim()) {
        toast.error('Please enter HTML content');
        return;
      }
    } else {
      if (!formData.content?.trim()) {
        toast.error('Please enter message content');
        return;
      }
    }
    
    if (!formData.subject?.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!formData.event_id && !formData.include_all_events) {
      toast.error('Please select an event or choose to send to all customers');
      return;
    }

    if (!confirm(`Are you sure you want to send this email to ${recipientCount} recipients?`)) {
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/admin/bulk-email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      // Check both response.ok and data.success
      if (response.ok && data.success) {
        toast.success(`Sent ${data.sent_count} emails successfully${data.failed_count > 0 ? ` (${data.failed_count} failed)` : ''}`);
        setFormData({ 
          event_id: '', 
          include_all_events: false, 
          subject: '', 
          header_text: 'Announcement',
          content: '',
          html_content: '' 
        });
        fetchData();
      } else if (response.ok && data.sent_count > 0) {
        // Partial success - some emails sent
        toast.success(`Sent ${data.sent_count} emails${data.failed_count > 0 ? ` (${data.failed_count} failed)` : ''}`);
        setFormData({ 
          event_id: '', 
          include_all_events: false, 
          subject: '', 
          header_text: 'Announcement',
          content: '',
          html_content: '' 
        });
        fetchData();
      } else {
        toast.error(data.message || data.detail || 'Failed to send emails');
      }
    } catch (error) {
      console.error('Bulk email error:', error);
      toast.error('Failed to send emails - network error');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#1a1f16] min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#c5bfa9]">Bulk Email</h1>
        <p className="text-[#8b9a7e] mt-1">Send emails to event attendees</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Form */}
        <div className="lg:col-span-2 bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
          <h2 className="text-lg font-semibold text-[#c5bfa9] mb-4">Compose Email</h2>

          {/* Recipients */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Recipients</label>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="all-events"
                  checked={formData.include_all_events}
                  onChange={(e) => setFormData({ ...formData, include_all_events: e.target.checked, event_id: '' })}
                  className="w-4 h-4 rounded border-[#3d4a35] bg-[#1a1f16] text-[#5a6f4e] focus:ring-[#5a6f4e]"
                />
                <label htmlFor="all-events" className="ml-2 text-[#c5bfa9]">
                  Send to all customers (across all events)
                </label>
              </div>
              
              {!formData.include_all_events && (
                <select
                  value={formData.event_id}
                  onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] focus:border-[#5a6f4e] focus:outline-none"
                >
                  <option value="">Select an event...</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name} - {event.date}
                    </option>
                  ))}
                </select>
              )}
              
              <div className="flex items-center text-sm text-[#8b9a7e]">
                <Users className="w-4 h-4 mr-2" />
                {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} will receive this email
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Subject</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-2 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] focus:border-[#5a6f4e] focus:outline-none"
              placeholder="Email subject..."
            />
          </div>

          {/* Placeholders */}
          <div className="mb-4 p-3 bg-[#1a1f16] rounded-lg border border-[#3d4a35]">
            <p className="text-xs text-[#8b9a7e] mb-2">Available placeholders:</p>
            <div className="flex flex-wrap gap-2">
              {['customer_name', 'event_name'].map((placeholder) => (
                <code 
                  key={placeholder}
                  className="px-2 py-1 bg-[#2d3728] text-[#c5bfa9] text-xs rounded cursor-pointer hover:bg-[#3d4a35]"
                  onClick={() => {
                    navigator.clipboard.writeText(`{{${placeholder}}}`);
                    toast.success(`Copied {{${placeholder}}}`);
                  }}
                >
                  {`{{${placeholder}}}`}
                </code>
              ))}
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="mb-4 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setUseAdvancedMode(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                !useAdvancedMode 
                  ? 'bg-[#4a5d42] text-white' 
                  : 'bg-[#1a1f16] text-[#8b9a7e] hover:bg-[#2d3728]'
              }`}
            >
              <FileText className="w-4 h-4" />
              Simple Mode
            </button>
            <button
              type="button"
              onClick={() => setUseAdvancedMode(true)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                useAdvancedMode 
                  ? 'bg-[#4a5d42] text-white' 
                  : 'bg-[#1a1f16] text-[#8b9a7e] hover:bg-[#2d3728]'
              }`}
            >
              <Code className="w-4 h-4" />
              HTML Mode
            </button>
          </div>

          {/* Simple Mode Content */}
          {!useAdvancedMode && (
            <>
              {/* Header Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Email Header Title</label>
                <input
                  type="text"
                  value={formData.header_text}
                  onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] focus:border-[#5a6f4e] focus:outline-none"
                  placeholder="Announcement"
                />
                <p className="text-xs text-[#6b7a5e] mt-1">This appears as the header title in the email</p>
              </div>

              {/* Plain Text Content */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Message Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={10}
                  className="w-full px-4 py-3 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] focus:border-[#5a6f4e] focus:outline-none"
                  placeholder={`Hello {{customer_name}},\n\nWe wanted to let you know about an important update...\n\nBest regards,\nFallen Dragon Events`}
                />
                <p className="text-xs text-[#6b7a5e] mt-1">Write in plain text - it will be styled automatically with the Fallen Dragon theme</p>
              </div>
            </>
          )}

          {/* HTML Mode Content */}
          {useAdvancedMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Email Content (Raw HTML)</label>
              <textarea
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                rows={12}
                className="w-full px-4 py-3 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] font-mono text-sm focus:border-[#5a6f4e] focus:outline-none"
                placeholder="<p>Hello {{customer_name}},</p>&#10;&#10;<p>Your email content here...</p>"
              />
              <p className="text-xs text-[#6b7a5e] mt-1">Full HTML control - write your own email template</p>
            </div>
          )}

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={sending || recipientCount === 0}
            className="w-full bg-[#4a5d42] hover:bg-[#5a6f4e] text-white"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send to {recipientCount} Recipients
              </>
            )}
          </Button>
        </div>

        {/* Send History */}
        <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
          <div className="flex items-center mb-4">
            <History className="w-5 h-5 mr-2 text-[#8b9a7e]" />
            <h2 className="text-lg font-semibold text-[#c5bfa9]">Send History</h2>
          </div>
          
          {logs.length === 0 ? (
            <p className="text-[#6b7a5e] text-sm">No emails sent yet</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="p-3 bg-[#1a1f16] rounded-lg border border-[#3d4a35]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#c5bfa9] truncate">{log.subject}</p>
                      <p className="text-xs text-[#6b7a5e] mt-1">
                        {log.event_name || 'All Events'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[#8b9a7e]">
                      {log.sent_count}/{log.recipients_count} sent
                    </span>
                    <span className="text-[#6b7a5e]">{formatDate(log.created_at)}</span>
                  </div>
                  {log.failed_count > 0 && (
                    <p className="text-xs text-red-400 mt-1">{log.failed_count} failed</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBulkEmail;
